import { getBracketMatchLabel } from "../../../utils/fixtureHelpers";

// Sentinel for "no link / standalone custom match" — selected when the user
// doesn't want the fixture tied to any existing slot. Both modals use this
// same value so they can swap selections without translating.
export const NO_LINK_VALUE = "__none__";

/**
 * Build the flat list of "link target" options for a given phase. The list
 * has a single "No link" sentinel at the top followed by every linkable
 * slot in the phase:
 *
 *   - GROUP blocks                 → one option each (label: "Group: <name>")
 *   - BRACKET blocks               → one option per round-fixture (per leg)
 *                                    (label: "Bracket: <Round> — <TieLabel>" + leg suffix)
 *   - SINGLE_MATCH non-custom      → one option each (label: "Single match: <name>")
 *
 * The Custom Matches block is intentionally NOT listed — picking "No link"
 * lands the fixture there. BRACKET blocks themselves aren't listed as a
 * single option (only their individual tie fixtures are).
 *
 * Each non-sentinel option carries enough metadata for the modal to:
 *   - render a label
 *   - decide team-picker scope (`group` members vs `category` pool)
 *   - filter out ineligible options when editing an existing fixture whose
 *     teams must be present in the destination GROUP (eligibility check is
 *     left to the caller; the helper just provides the data)
 *   - build the create/update payload (blockId, optional fixtureId)
 */
export const buildSlotOptions = (phase) => {
  const noLink = {
    label: "No link (standalone custom match)",
    value: NO_LINK_VALUE,
    kind: "noLink",
  };

  if (!phase) return [noLink];

  const blocks = phase._phase?.blocks || phase.blocks || [];
  const opts = [noLink];

  for (const block of blocks) {
    if (!block || block.isCustom) continue;

    const blockId = (block._id || block.id)?.toString?.();

    if (block.type === "GROUP") {
      opts.push({
        label: `Group: ${block.name}`,
        value: `group:${blockId}`,
        kind: "group",
        blockId,
        block,
        teamPickerScope: "group",
      });
    } else if (block.type === "SINGLE_MATCH") {
      opts.push({
        label: `Single match: ${block.name}`,
        value: `single:${blockId}`,
        kind: "singleMatch",
        blockId,
        block,
        teamPickerScope: "category",
      });
    } else if (block.type === "BRACKET") {
      // Each round-fixture is its own pickable slot (per leg). Tie labels
      // are computed the same way as the rest of the app (QF-1, SF-2, …).
      for (const round of block.rounds || []) {
        const tiePos = new Map();
        const fixtures = round.fixtures || [];
        fixtures.forEach((f) => {
          let positionInRound;
          if (f.tieNumber != null) {
            if (!tiePos.has(f.tieNumber)) tiePos.set(f.tieNumber, tiePos.size + 1);
            positionInRound = tiePos.get(f.tieNumber);
          } else {
            tiePos.set(Symbol(), tiePos.size + 1);
            positionInRound = tiePos.size;
          }
          const tieLabel = f.name || getBracketMatchLabel(round.name, positionInRound);
          const legSuffix = f.leg ? ` Leg ${f.leg}` : "";
          const fixtureId = (f._id || f.id)?.toString?.();
          opts.push({
            label: `Bracket: ${round.name} — ${tieLabel}${legSuffix}`,
            value: `bracket:${blockId}:${fixtureId}`,
            kind: "bracket",
            blockId,
            fixtureId,
            block,
            tieFixture: f,
            teamPickerScope: "category",
          });
        });
      }
    }
  }

  return opts;
};

/**
 * Look up the slot option that corresponds to a fixture's *current* location
 * in the tournament tree. Used by EditFixtureModal to initialise its
 * selector from the existing fixture without ambiguity.
 *
 * Returns the matching option, or `null` (which the caller interprets as
 * "the No link sentinel" — fixture is in the Custom Matches block).
 */
export const findCurrentSlotOption = (slotOptions, fixture) => {
  if (!fixture || !slotOptions?.length) return null;
  const fixturePhaseId = fixture.phaseId?.toString?.();
  const fixtureBlockId = fixture.blockId?.toString?.();
  const fixtureId = (fixture._id || fixture.id)?.toString?.();

  for (const opt of slotOptions) {
    if (opt.kind === "noLink") continue;
    if (opt.blockId !== fixtureBlockId) continue;
    if (opt.kind === "bracket") {
      if (opt.fixtureId === fixtureId) return opt;
    } else {
      return opt;
    }
    // Suppress unused phase id warning — kept for future per-phase scoping.
    void fixturePhaseId;
  }
  return null;
};

/**
 * Determine whether a given option is selectable for a fixture with the
 * given home/away team ids. Used to filter the dropdown in EditFixtureModal.
 *
 * - GROUP: both teams must be non-dummy members of `block.teams[]`.
 * - BRACKET / SINGLE_MATCH non-custom: any team in the category — always eligible.
 * - noLink: always eligible.
 */
export const isSlotEligible = (opt, { homeTeamId, awayTeamId }) => {
  if (!opt) return false;
  if (opt.kind === "noLink") return true;
  if (opt.kind !== "group") return true;

  const teams = opt.block?.teams || [];
  const memberIds = teams
    .filter((t) => !t.isDummy && t.actualTeamId)
    .map((t) => (t.actualTeamId?._id || t.actualTeamId)?.toString?.());

  const hId = homeTeamId?.toString?.();
  const aId = awayTeamId?.toString?.();
  return !!hId && !!aId && memberIds.includes(hId) && memberIds.includes(aId);
};
